import countBy from 'lodash/countBy';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import trim from 'lodash/trim';
import unset from 'lodash/unset';

import routes from '../../config/routes.json';

const PORTAL_SLUG = 'portal';

const queryStringToObject = (queryString) => {
  const parameters = Object.create(null);

  new URLSearchParams(queryString).forEach((value, key) => {
    parameters[key] = value;
  });

  return parameters;
};

const toHrefObject = (href) => {
  if (isNil(href)) {
    // eslint-disable-next-line no-console
    console.warn(
      'href should have be a valid string or object with a pathname key'
    );

    return {
      pathname: '/',
      query: {},
    };
  }

  if (isString(href)) {
    const [pathname, queryParameters] = href.split('?');

    const query = queryParameters ? queryStringToObject(queryParameters) : {};

    return {
      pathname,
      query,
    };
  }

  if (!href.pathname) {
    throw new Error('href should have a pathname if given as an object');
  }

  if (!href.query) {
    return {
      ...href,
      query: {},
    };
  }

  return href;
};

const countParts = (pathname) => countBy(pathname)['/'] || 0;

const toParts = (href) =>
  href.pathname
    .split('/')
    .filter(Boolean)
    .map((part) => trim(part, '/'));

const fromParts = (parts) => ['', ...parts].join('/');

const pathnameToAs = (pathname, { ...query }) => {
  const as = pathname.replace(/\[.*?\]/g, (match) => {
    const parameterName = match.slice(1, -1);
    const parameterValue = query[parameterName];
    unset(query, parameterName);

    return parameterValue;
  });

  const queryString = new URLSearchParams(query).toString();

  if (queryString) {
    return `${as}?${queryString}`;
  }

  return as;
};

const replaceWithParams = (originalPathname) =>
  routes.reduce((pathname, route) => {
    if (pathname) {
      return pathname;
    }

    const regexp = new RegExp(
      `^${route.replace(/\//g, '\\/').replace(/\[.*?\]/g, () => `(.+)`)}$`
    );

    const matchedRoute = originalPathname.match(regexp);
    if (matchedRoute && countParts(route) === countParts(originalPathname)) {
      return route;
    }

    return pathname;
  }, null) || originalPathname;

const formatHrefObject = (pathname, query, as, brandSlug) => {
  const isHomepage = pathname === '/[brandSlug]' && brandSlug === PORTAL_SLUG;
  const formattedPathname = isHomepage ? '/' : pathname;
  const formattedAs = as === `/${PORTAL_SLUG}` ? '/' : as;

  if (isHomepage) {
    unset(query, 'brandSlug');
  }

  return {
    href: {
      pathname: formattedPathname || '/',
      query,
    },
    as: formattedAs,
  };
};

const prepareHref = (
  originalHref,
  brand = { slug: PORTAL_SLUG },
  brandSlugs
) => {
  const brandSlug = brand.slug ?? PORTAL_SLUG;
  const href = toHrefObject(originalHref);

  const parts = toParts(href);

  const isFirstPartValidBrandSlug = brandSlugs
    ? !brandSlugs.includes(parts[0])
    : parts[0] !== brandSlug;

  if (parts[0] === PORTAL_SLUG) {
    parts.shift();
  } else if (
    brandSlug !== PORTAL_SLUG &&
    isFirstPartValidBrandSlug &&
    parts[0] !== '[brandSlug]'
  ) {
    parts.unshift(brandSlug);
  }

  const pathname = fromParts(parts);

  const as = pathnameToAs(pathname, href.query);

  const paramatizedPathname = replaceWithParams(pathname);

  return formatHrefObject(paramatizedPathname, href.query, as, brandSlug);
};

export default prepareHref;
